// /visa-compliance — TH hub for TM30, 90-day report, re-entry, overstay, TDAC, etc.
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ComplianceCategory } from "@/data/visa-compliance-topics";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { ShieldCheck, Phone, MessageCircle, ArrowRight, Sparkles, AlertTriangle, FileText, Clock, Stamp } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/visa-compliance/")({
    loader: async () => {
    const { COMPLIANCE_TOPICS, COMPLIANCE_CATEGORY_LABELS_TH } = await import("@/data/visa-compliance-topics");
    return { __d: { COMPLIANCE_TOPICS, COMPLIANCE_CATEGORY_LABELS_TH } };
  },
head: ({ loaderData }) => {
    const { COMPLIANCE_TOPICS, COMPLIANCE_CATEGORY_LABELS_TH } = loaderData!.__d as typeof import("@/data/visa-compliance-topics");
    const url = `${SITE}/visa-compliance`;
    const title = `รวมข้อบังคับวีซ่าและที่พักในไทย — TM30, 90-Day, Re-Entry, Overstay, TDAC | NYC`;
    const desc = `คู่มือ Visa Compliance ครบทุกหัวข้อสำหรับคนต่างชาติในไทย — TM30, 90-day report (TM47), Re-entry Permit, Overstay, TDAC, Certificate of Residence, ต่อวีซ่า พร้อมขั้นตอน ค่าธรรมเนียม กำหนดเวลา และ legal reference ทุกฉบับ — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(title, "/visa-compliance", "th") }, { name: "description", content: ctaDesc(desc, "/visa-compliance", "th")},
        { property: "og:title", content: ctaTitle(title, "/visa-compliance", "th")}, { property: "og:description", content: ctaDesc(desc, "/visa-compliance", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
        { name: "keywords", content: "TM30, 90 day report, TM47, re-entry permit, overstay, TDAC, visa extension Thailand, certificate of residence, immigration compliance" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/visa-compliance", "/en/visa-compliance")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc, url, inLanguage: "th-TH",
              mainEntity: {
                "@type": "ItemList", numberOfItems: COMPLIANCE_TOPICS.length,
                itemListElement: COMPLIANCE_TOPICS.map((t, i) => ({
                  "@type": "ListItem", position: i + 1, name: t.titleTh,
                  url: `${SITE}/visa-compliance/${t.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Thailand Visa Compliance & Immigration Reporting Service",
              serviceType: "Thailand Visa Compliance & Immigration Reporting Service",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "1500", priceCurrency: "THB", description: "TM30 / 90-day / TM28 filing service" },
            }),
          },
          faqPageSchema([
            { q: "TM30 กับ TM28 ต่างกันอย่างไร?", a: "TM30 = เจ้าบ้านแจ้งคนต่างชาติเข้าพักภายใน 24 ชั่วโมง · TM28 = คนต่างชาติแจ้งย้ายที่อยู่ใหม่ภายใน 24 ชั่วโมง สองตัวต้องทำคู่กันเมื่อย้ายที่พัก" },
            { q: "90-day report ยื่นออนไลน์ได้ไหม?", a: "ได้ ที่ tm47.immigration.go.th ตาม พ.ร.บ.คนเข้าเมือง 2522 มาตรา 37(5) — ยกเว้นรายงานครั้งแรกที่ต้อง walk-in ที่ตม. ครั้งหนึ่งเพื่อ biometric registration (ลายนิ้วมือ 10 นิ้ว + รูปถ่าย) ยื่นได้ภายใน 15 วันก่อน-7 วันหลังครบ 90 วัน หากเลยปรับ 2,000 บาท" },
            { q: "ออกประเทศโดยไม่ขอ Re-entry permit มีผลอะไร?", a: "วีซ่าโมฆะทันทีตาม พ.ร.บ.คนเข้าเมือง 2522 มาตรา 39 — ต้องเริ่มขอ Non-Immigrant Visa ใหม่จากสถานทูตต่างประเทศ เสียค่าธรรมเนียม 2,000–5,000 บาท + รออนุมัติ 5–15 วันทำการ ไม่มีทางอุทธรณ์ — Single Re-entry 1,000฿, Multiple 3,800฿" },
            { q: "Overstay 1 วันถูก blacklist ไหม?", a: "ไม่ — blacklist ตามประกาศสำนักงานตม. 2/2559 เริ่มที่ overstay เกิน 90 วันแบบ self-report (แบน 1 ปี) หรือทุกกรณีที่ถูกจับกุม (แบน 5–10 ปี) — overstay 1 วันปรับ 500 บาท/วัน สูงสุด 20,000 บาท พร้อมประทับตรา overstay ในพาสปอร์ต" },
            { q: "TDAC จำเป็นทุกครั้งที่เข้าประเทศไทยไหม?", a: "ใช่ — ตั้งแต่ 1 พ.ค. 2025 ตามประกาศสตม. 1/2568 ใช้ Thailand Digital Arrival Card (TDAC) แทน TM6 กระดาษ 100% สำหรับชาวต่างชาติทุกประเภทรวม Visa Exemption ต้องกรอกที่ tdac.immigration.go.th ภายใน 72 ชั่วโมงก่อนถึง รับ QR Code แสดงต่อ ตม." },

          ]),
        ]),
      ],
    };
  },
  component: ComplianceHubTh,
});

function ComplianceHubTh() {
  const { COMPLIANCE_TOPICS, COMPLIANCE_CATEGORY_LABELS_TH } = (Route.useLoaderData() as { __d: typeof import("@/data/visa-compliance-topics") }).__d;
  const byCat = COMPLIANCE_TOPICS.reduce<Record<string, typeof COMPLIANCE_TOPICS>>((acc, t) => {
    (acc[t.category] ||= []).push(t); return acc;
  }, {});
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <span>Visa Compliance</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Stamp className="mr-1 inline h-3 w-3" /> Immigration · Compliance · {COMPLIANCE_TOPICS.length} หัวข้อ
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            รวมข้อบังคับวีซ่าและคนต่างชาติในไทย
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            TM30 · 90-Day Report (TM47) · Re-Entry Permit · TM28 · Overstay · TDAC · Certificate of Residence · ต่อวีซ่า — ทุกขั้นตอน ค่าธรรมเนียม และ legal reference อัปเดตล่าสุด 2026
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">คนต่างชาติในไทยต้องรู้อะไรบ้างเรื่อง Visa Compliance?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              5 สิ่งสำคัญ: (1) TM30 เจ้าบ้านแจ้งภายใน 24 ชม. (2) 90-Day Report ทุก 90 วัน ออนไลน์ฟรี (3) Re-entry Permit ก่อนออกประเทศ 1,000/3,800 บาท (4) TDAC ออนไลน์ก่อนเข้าไทย 72 ชม. ฟรี (5) ห้าม Overstay (500/วัน, blacklist 1-10 ปี) NYC บริการครบ TM30/TM47/TM28/Extension ครบ 8,000+ เคส
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/visa" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <FileText className="h-4 w-4" /> วีซ่าทุกประเภท
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">เลือกหัวข้อที่ต้องการ</h2>
          <p className="mt-2 text-sm text-muted-foreground">จัดกลุ่มตามประเภทกฎหมาย · คลิกเพื่อดูขั้นตอน ค่าธรรมเนียม กำหนดเวลา และเอกสาร</p>
          <div className="mt-8 space-y-10">
            {(Object.keys(byCat) as ComplianceCategory[]).map((cat) => (
              <div key={cat}>
                <h3 className="mb-4 text-lg font-bold text-primary">{COMPLIANCE_CATEGORY_LABELS_TH[cat]}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {byCat[cat].map((t) => (
                    <Link
                      key={t.slug}
                      to="/visa-compliance/$topic"
                      params={{ topic: t.slug }}
                      className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                      data-track-zone="compliance-hub-th"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{t.icon}</span>
                        <div className="font-bold text-sm">{t.titleTh}</div>
                      </div>
                      <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{t.taglineTh}</div>
                      <div className="mt-3 flex items-center justify-between text-xs">
                        <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">{t.deadlineTh}</span>
                        <span className="inline-flex items-center gap-1 font-semibold text-primary">
                          ดู <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-amber-500/10 px-4 py-10">
        <div className="mx-auto max-w-5xl rounded-2xl border-2 border-amber-500/40 bg-card p-6">
          <h2 className="text-xl font-extrabold text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mr-2 inline h-5 w-5" />คำเตือน Overstay & Blacklist
          </h2>
          <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
            <li>• <strong>Overstay ≤ 90 วัน</strong>: 500 บาท/วัน · cap 20,000 บาท · ไม่ blacklist (self-report)</li>
            <li>• <strong>90 วัน – 1 ปี</strong>: blacklist 1 ปี · <strong>1-3 ปี</strong>: blacklist 3 ปี</li>
            <li>• <strong>3-5 ปี</strong>: blacklist 5 ปี · <strong>5-10 ปี</strong>: blacklist 10 ปี · <strong>10+ ปี</strong>: ตลอดชีวิต</li>
            <li>• ถ้าถูกตำรวจจับในไทย: detention IDC Suan Phlu + deportation + cap หายไป</li>
          </ul>
          <Link to="/visa-compliance/$topic" params={{ topic: "overstay-fine" }} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-white hover:bg-amber-600">
            <Clock className="h-4 w-4" /> ดูรายละเอียด Overstay
          </Link>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />บริการที่เกี่ยวข้อง</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "DTV 5 ปี (Digital Nomad)", d: "Workcation · Freelancer · Muay Thai · Cooking · 9 ประเภท", to: "/dtv" as const },
              { t: "วีซ่าทุกประเภท", d: "Non-B / LTR / DTV / Retirement / Marriage", to: "/visa" as const },
              { t: "Work Permit + Non-B", d: "15 สัญชาติ BOI ฟาสต์แทร็ก", to: "/work-permit" as const },
              { t: "แลกใบขับขี่ DLT", d: "12 สัญชาติหลัก · ใช้ Certificate of Residence", to: "/driving-license" as const },
              { t: "แปลเอกสาร", d: "Passport, ทะเบียนสมรส, ใบรับรอง", to: "/translate" as const },
              { t: "Apostille / Legalisation", d: "60+ ประเทศ Hague + 16 non-Hague", to: "/apostille" as const },
              { t: "Notary Public", d: "POA, Affidavit, Embassy authentication", to: "/notary" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    </main>
  );
}
